import { Skeleton } from "@/components/shared/Skeleton";
import { TeacherPage } from "@/components/teacher/TeacherPage";
import { ClassroomGrid } from "@/components/teacher/classroom/ClassroomGrid";
import { AssignedHomeworkDocument } from "@/generated/graphql";
import { type TranslationKey, formatYearMonthDay, useLocale, useT } from "@/i18n";
import { useQuery } from "@apollo/client/react";
import { Link } from "@tanstack/react-router";
import { useState } from "react";

const TABS = [
  { key: "homework", label: "check.tabHomework" },
  { key: "classrooms", label: "nav.classrooms" },
] as const;

const COLUMNS: TranslationKey[] = [
  "check.column.title",
  "check.column.due",
  "check.column.assigned",
  "check.column.classroom",
];

const SKELETON_KEYS = ["a", "b", "c", "d", "e"];

function AssignedHomeworkTable() {
  const locale = useLocale();
  const t = useT();
  const { data, loading, error } = useQuery(AssignedHomeworkDocument);

  const rows = data?.assignedHomework ?? [];

  if (loading && rows.length === 0) {
    return (
      <div className="app-card divide-y divide-gray-100">
        {SKELETON_KEYS.map((key) => (
          <div key={key} className="p-4 sm:p-5">
            <Skeleton className="h-6" />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return <p className="app-card body-sm p-4 text-app-overdue sm:p-6">{t["check.loadError"]}</p>;
  }

  if (rows.length === 0) {
    return <p className="app-card body-sm p-4 text-app-muted sm:p-6">{t["check.empty"]}</p>;
  }

  return (
    <div className="app-card overflow-x-auto">
      <table className="w-full min-w-[40rem] border-collapse text-left">
        <thead>
          <tr className="border-gray-100 border-b">
            {COLUMNS.map((column) => (
              <th key={column} className="app-label px-4 py-3 font-normal sm:px-5">
                {t[column]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((row) => (
            <tr key={row.batchId} className="cursor-pointer hover:bg-app-sky-soft">
              <td className="font-kumbh font-extrabold text-app-ink">
                <Link
                  to="/teacher/check/$batchId"
                  params={{ batchId: row.batchId }}
                  className="block px-4 py-3 sm:px-5"
                >
                  {row.title}
                </Link>
              </td>
              <td className="app-value px-4 py-3 tabular-nums sm:px-5">
                {formatYearMonthDay(locale, new Date(row.dueAt))}
              </td>
              <td className="app-value px-4 py-3 tabular-nums sm:px-5">
                {formatYearMonthDay(locale, new Date(row.assignedAt))}
              </td>
              <td className="app-value px-4 py-3 sm:px-5">{row.classroom?.name ?? "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Reviewing starts either from what was assigned or from the class it went to, so: two tabs. */
export function CheckHomeworkPage() {
  const [tab, setTab] = useState<(typeof TABS)[number]["key"]>("homework");
  const t = useT();

  return (
    <TeacherPage title={t["nav.checkHomework"]}>
      <div
        role="tablist"
        aria-label={t["nav.checkHomework"]}
        className="flex w-fit gap-1 rounded-full border border-white/70 bg-white/60 p-1 backdrop-blur"
      >
        {TABS.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            onClick={() => setTab(key)}
            className={`rounded-full px-4 py-2 font-kumbh text-sm font-bold transition-colors ${
              tab === key ? "bg-app-blue text-white shadow-app-card" : "text-app-ink hover:bg-white"
            }`}
          >
            {t[label]}
          </button>
        ))}
      </div>

      {tab === "homework" ? <AssignedHomeworkTable /> : <ClassroomGrid />}
    </TeacherPage>
  );
}
